import React from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";

import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";

import { FaRightFromBracket } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { removeUser, setUser } from "../../redux/userSlice.js";
import { logout } from "helpers/firebase/authService.js";
import { getUser } from "helpers/firebase/userService.js";
import { useEffect } from "react";
import Loader from "components/Loader.jsx";

const Header = tw.header`
  flex justify-between items-center
  max-w-screen-xl mx-auto
`;

export const NavLinks = tw.div`inline-block`;

/* hocus: stands for "on hover or focus"
 * hocus:bg-primary-700 will apply the bg-primary-700 class on hover or focus
 */
export const NavLink = tw.a`
  text-sm my-2 md:mx-4 md:my-0 lg:mx-6 lg:text-base
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
`;

export const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-primary-500 text-gray-100
  hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};

  img {
    ${tw`w-10 mr-3`}
  }
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between`;
export const NavToggle = tw.button`
  md:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;
export const MobileNavLinks = motion(styled.div`
  ${tw`md:hidden z-10 fixed top-0 left-0 h-[100dvh] w-full inset-x-0 p-8 border text-center rounded-lg text-gray-900 bg-white`}
  ${NavLinks} {
    ${tw`flex flex-col items-center`}
  }
`);

export const DesktopNavLinks = tw.nav`
  hidden md:flex flex-1 justify-between items-center
`;

export default ({
  roundedHeaderButton = true,
  logoLink,
  links,
  className,
  collapseBreakpointClass = "md",
}) => {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);

  const { userId } = useSelector((state) => state.user);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      if (userId) {
        try {
          const userDoc = await getUser(userId);
          dispatch(setUser(userDoc));
          setUserData(userDoc);
          setUser(userDoc);
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    };

    fetchUserData().finally(() => setIsLoading(false));
  }, [userId, dispatch]);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleLogout = async () => {
    try {
      await logout();
      dispatch(removeUser());
      navigate("/auth/sign-in");
    } catch (error) {
      alert("Logout Failed: " + error.message);
    } finally {
      setShowDropdown(false);
    }
  };

  const defaultLinks = [
    <NavLinks key={1}>
      <NavLink href="#">Recipes</NavLink>
      <NavLink href="/diets">Diets</NavLink>
      <NavLink href="#">Shop</NavLink>
      <NavLink href="#">Blog</NavLink>
      {isLoading || userData ? (
        <div className="relative inline-block">
          {isLoading ? (
            <Loader size="small" />
          ) : (
            <>
              <button onClick={toggleDropdown} tw="hidden md:block lg:ml-12!">{`Hello! ${userData?.username || userData?.email || "John Doe"
                }`}</button>

              <button
                onClick={handleLogout}
                className="md:hidden max-w-[90%] m-2 w-40 flex items-center justify-center h-10 border border-red-600 rounded-full text-sm font-semibold hover:text-red-400"
              >
                Logout
              </button>

              {showDropdown && (
                <div className="w-[9rem] absolute top-[2rem] right-0 shadow-lg">
                  <ul className="h-full overflow-y-auto bg-gray-200">
                    <li
                      onClick={handleLogout}
                      className="h-10 shadow-sm flex items-center justify-between px-3 cursor-pointer"
                    >
                      <span className="text-sm capitalize font-semibold">
                        Logout
                      </span>
                      <FaRightFromBracket color="crimson" size={14} />
                    </li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <NavLink href="/auth/sign-in" tw="lg:ml-12!">
            Login
          </NavLink>
          <PrimaryLink
            css={roundedHeaderButton && tw`rounded-full`}
            href="/auth/sign-up"
          >
            Sign Up
          </PrimaryLink>
        </>
      )}
    </NavLinks>,
  ];

  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss =
    collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink href="/">
      <img src="/logo.png" alt="logo" />
      <span className="capitalize text-sm sm:text-md md:text-xl">
        Help yourself goals
      </span>
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <Header className={className || "header-light"}>
      <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
        {logoLink}
        {links}
      </DesktopNavLinks>

      <MobileNavLinksContainer
        css={collapseBreakpointCss.mobileNavLinksContainer}
      >
        {logoLink}
        <MobileNavLinks
          initial={{ x: "150%", display: "none" }}
          animate={animation}
          css={collapseBreakpointCss.mobileNavLinks}
        >
          <div className="flex items-center justify-center flex-1">
            {logoLink}
          </div>
          {links}
        </MobileNavLinks>
        <NavToggle
          onClick={toggleNavbar}
          className={showNavLinks ? "open" : "closed"}
        >
          {showNavLinks ? (
            <CloseIcon tw="w-6 h-6" />
          ) : (
            <MenuIcon tw="w-6 h-6" />
          )}
        </NavToggle>
      </MobileNavLinksContainer>
    </Header>
  );
};

/* The below code is for generating dynamic break points for navbar.
 * Using this you can specify if you want to switch
 * to the toggleable mobile navbar at "sm", "md" or "lg" or "xl" above using the collapseBreakpointClass prop
 * Its written like this because we are using macros and we can not insert dynamic variables in macros
 */

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`,
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`,
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
};
